/**
 * TanStack Query hooks — Comparison Notes MVP (Chunk 9.Q.2).
 *
 * Backend (humain-only Phase 1, cf. apps/api/src/routes/comparison-notes.ts) :
 *   GET    /v1/projects/:id/comparison-notes[?field_key=&submission_id=]   list nested
 *   POST   /v1/comparison-notes                                            create
 *   PATCH  /v1/comparison-notes/:id                                        update body
 *   DELETE /v1/comparison-notes/:id                                        soft-delete
 *
 * Wire shapes :
 *   LIST   : `data = { items: ComparisonNoteRow[] }`
 *   POST   : `data = ComparisonNoteRow` (201)
 *   PATCH  : `data = ComparisonNoteRow` (200)
 *   DELETE : `data = { id: string, deleted: true }`
 *
 * Codes erreur attendus côté UI (bilingues) :
 *   COMPARISON_NOTES_NOT_FOUND                     (404)
 *   COMPARISON_NOTES_PROJECT_NOT_FOUND             (404 anti-leak)
 *   COMPARISON_NOTES_SUBMISSION_PROJECT_MISMATCH   (422)
 *   COMPARISON_NOTES_SCOPE_INVALID                 (422)
 *   COMPARISON_NOTES_FORBIDDEN_USER_ONLY           (403, wording neutre)
 *   VALIDATION_ERROR                               (400)
 *
 * Convention queryKey :
 *   ['comparison-notes', 'by-project', { projectId, fieldKey, submissionId }]
 *
 * Invalidation post-mutation : queryKey ['comparison-notes', 'by-project']
 * (ratisser large — Phase 1 cardinalité faible, < 50 notes/projet attendu).
 */

import type {
  ComparisonNoteRow,
  CreateComparisonNoteInput,
  UpdateComparisonNoteInput,
} from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";

// -----------------------------------------------------------------------------
// Types — re-export ergonomiques
// -----------------------------------------------------------------------------

export type ComparisonNotesListResult = {
  items: ComparisonNoteRow[];
};

export type ComparisonNotesParams = {
  projectId: string | undefined;
  fieldKey?: string | null;
  submissionId?: string | null;
  enabled?: boolean;
};

// -----------------------------------------------------------------------------
// GET /v1/projects/:id/comparison-notes — list nested
// -----------------------------------------------------------------------------

function buildListPath(params: ComparisonNotesParams): string {
  const base = `/v1/projects/${params.projectId}/comparison-notes`;
  const sp = new URLSearchParams();
  if (params.fieldKey) sp.set("field_key", params.fieldKey);
  if (params.submissionId) sp.set("submission_id", params.submissionId);
  const qs = sp.toString();
  return qs ? `${base}?${qs}` : base;
}

export function useComparisonNotes(params: ComparisonNotesParams) {
  return useQuery({
    queryKey: [
      "comparison-notes",
      "by-project",
      {
        projectId: params.projectId,
        fieldKey: params.fieldKey ?? null,
        submissionId: params.submissionId ?? null,
      },
    ],
    queryFn: () =>
      api.get<ComparisonNotesListResult>(buildListPath(params)),
    enabled: (params.enabled ?? true) && !!params.projectId,
    placeholderData: (prev) => prev,
  });
}

// -----------------------------------------------------------------------------
// Mutations — POST + PATCH + DELETE 9.Q.2 broker-facing
// -----------------------------------------------------------------------------

function invalidateLists(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ["comparison-notes", "by-project"] });
}

/** Vars POST /v1/comparison-notes — create note (humain-only). */
export type CreateComparisonNoteMutationVars = {
  input: CreateComparisonNoteInput;
};

export function useCreateComparisonNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ input }: CreateComparisonNoteMutationVars) =>
      api.post<ComparisonNoteRow>(
        "/v1/comparison-notes",
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: () => {
      invalidateLists(qc);
    },
  });
}

/** Vars PATCH /v1/comparison-notes/:id — update body (scope immuable). */
export type UpdateComparisonNoteMutationVars = {
  noteId: string;
  input: UpdateComparisonNoteInput;
};

export function useUpdateComparisonNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId, input }: UpdateComparisonNoteMutationVars) =>
      api.patch<ComparisonNoteRow>(
        `/v1/comparison-notes/${noteId}`,
        input as unknown as Record<string, unknown>,
      ),
    onSuccess: () => {
      invalidateLists(qc);
    },
  });
}

/** Vars DELETE /v1/comparison-notes/:id — soft-delete (UPDATE deleted_at). */
export type DeleteComparisonNoteMutationVars = {
  noteId: string;
};

export function useDeleteComparisonNote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ noteId }: DeleteComparisonNoteMutationVars) =>
      api.del<{ id: string; deleted: true }>(`/v1/comparison-notes/${noteId}`),
    onSuccess: () => {
      invalidateLists(qc);
    },
  });
}
